import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EXIT_CRITERION_KINDS, type ExitCriterionInput } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { projectConfigApi } from '../../../lib/project-config-api.js';
import { useCurrentProject } from '../../../lib/use-project.js';

// Plan 7 / Task F3: Editor für REP-5-Exit-Kriterien (Go/No-Go-Checkliste). `no_open_blocker`/
// `no_open_high` sind reine Ja/Nein-Kriterien ("keine offenen Blocker/High") — für die macht ein
// Schwellwert keinen Sinn, deshalb ist `threshold` in diesen beiden Fällen gesperrt + wird beim
// Umschalten auf null gesetzt (Server-Schema erlaubt dort ohnehin nur null).
type ExitCriterionRow = ExitCriterionInput & { _localId: string };

let localIdSeq = 0;
function nextLocalId(): string {
  localIdSeq += 1;
  return `exit-new-${localIdSeq}`;
}

function withSortOrder(rows: ExitCriterionRow[]): ExitCriterionRow[] {
  return rows.map((r, i) => ({ ...r, sort_order: i }));
}

function toPayload(row: ExitCriterionRow): ExitCriterionInput {
  const { _localId, ...rest } = row;
  return rest;
}

function thresholdLocked(kind: ExitCriterionInput['kind']): boolean {
  return kind === 'no_open_blocker' || kind === 'no_open_high';
}

const inputClass =
  'w-full rounded-btn border border-[var(--border)] bg-bg-surface px-1.5 py-1 text-sm text-text-body';

export function ExitCriteriaPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;

  const query = useQuery({
    queryKey: ['config-exit-criteria', projectId],
    queryFn: () => projectConfigApi.getExitCriteria(projectId ?? ''),
    enabled: Boolean(projectId),
  });

  const [rows, setRows] = useState<ExitCriterionRow[]>([]);

  useEffect(() => {
    if (!query.data) return;
    setRows(
      query.data.map((c) => ({
        key: c.key,
        label_de: c.label_de,
        label_en: c.label_en,
        kind: c.kind,
        threshold: c.threshold,
        sort_order: c.sort_order,
        _localId: c.criterion_id,
      })),
    );
  }, [query.data]);

  const save = useMutation({
    mutationFn: (items: ExitCriterionInput[]) =>
      projectConfigApi.putExitCriteria(projectId ?? '', items),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-exit-criteria', projectId] });
    },
  });

  function updateRow(localId: string, patch: Partial<ExitCriterionRow>) {
    setRows((prev) => prev.map((r) => (r._localId === localId ? { ...r, ...patch } : r)));
  }

  function setKind(localId: string, kind: ExitCriterionInput['kind']) {
    setRows((prev) =>
      prev.map((r) =>
        r._localId === localId
          ? { ...r, kind, threshold: thresholdLocked(kind) ? null : r.threshold }
          : r,
      ),
    );
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        _localId: nextLocalId(),
        key: '',
        label_de: '',
        label_en: '',
        kind: 'coverage_pct',
        threshold: 100,
        sort_order: prev.length,
      },
    ]);
  }

  function removeRow(localId: string) {
    setRows((prev) => prev.filter((r) => r._localId !== localId));
  }

  if (projectLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project || !projectId) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-text-primary">{t('admin.exit.navLabel')}</h2>
        <button
          type="button"
          onClick={addRow}
          className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10"
        >
          + {t('admin.exit.add')}
        </button>
      </div>

      {query.isLoading && <div className="text-sm text-text-muted">{t('common.loading')}</div>}
      {!query.isLoading && rows.length === 0 && (
        <div className="text-sm text-text-muted">{t('admin.exit.empty')}</div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-card border border-[var(--border)] bg-bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
                <th className="px-2 py-2 text-left">{t('admin.exit.columns.key')}</th>
                <th className="px-2 py-2 text-left">{t('admin.exit.columns.labelDe')}</th>
                <th className="px-2 py-2 text-left">{t('admin.exit.columns.labelEn')}</th>
                <th className="px-2 py-2 text-left">{t('admin.exit.columns.kind')}</th>
                <th className="px-2 py-2 text-left">{t('admin.exit.columns.threshold')}</th>
                <th className="w-8 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._localId} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-2 py-1.5">
                    <input
                      value={row.key}
                      onChange={(e) => updateRow(row._localId, { key: e.target.value })}
                      className={inputClass}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      value={row.label_de}
                      onChange={(e) => updateRow(row._localId, { label_de: e.target.value })}
                      className={inputClass}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      value={row.label_en}
                      onChange={(e) => updateRow(row._localId, { label_en: e.target.value })}
                      className={inputClass}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      value={row.kind}
                      onChange={(e) =>
                        setKind(row._localId, e.target.value as ExitCriterionInput['kind'])
                      }
                      className={inputClass}
                    >
                      {EXIT_CRITERION_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {t(`admin.exit.kinds.${k}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      value={row.threshold ?? ''}
                      disabled={thresholdLocked(row.kind)}
                      onChange={(e) =>
                        updateRow(row._localId, {
                          threshold: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className={`${inputClass} w-20 disabled:opacity-50`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => removeRow(row._localId)}
                      aria-label={t('admin.exit.remove')}
                      title={t('admin.exit.remove')}
                      className="rounded-btn px-1 text-text-muted hover:text-status-fail"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => save.mutate(withSortOrder(rows).map(toPayload))}
          disabled={save.isPending}
          className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t('admin.exit.save')}
        </button>
      </div>
    </div>
  );
}
